import empty from "is-empty";
import { Tx } from "./types";
import { COLLAT_TYPES, FETCH_STATES, LOAN_TYPES, TX_STATES } from "./constants";
import { TxSelectors } from "./selectors";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import fetchWrapper from "src/utils/fetchWrapper";
import { ModalActions } from "src/redux/Modal/actions";
import { InventoryActions } from "src/redux/Inventory/actions";
import { getFromLocalUserStorage } from "src/utils/localStorage";
import { setDateFormat } from "src/utils";
import { InventorySelectors } from "src/redux/Inventory/selectors";
import { ToasterActions } from "src/redux/Toaster/actions";
import { SpinnerActions } from "src/redux/Spinner/actions";
import * as Validators from "src/utils/validators";
import { isNumber } from "mathjs";
import { AvailabilitySelectors } from "src/redux/Availability/selectors";
import { AvailabilityActions } from "src/redux/Availability/actions";
import { ROLE, VIEWS } from "src/redux/TopBar/constants";
import Math from "src/utils/math";
import UploadNeedsPreview from "src/components/views/Transactions/Create/Preview";
import SearchAvailabilityPreview from "src/components/views/Availability/Take";
import {
    mapBorrowEntry,
    mapLendEntry,
    updateBorrowerEntries,
    updateLendEntries,
} from "./reducer";
import {
    getClampedMaturityDate,
    getDefaultTxPurposeForCountryCode,
} from "src/utils";
import { ConfirmActions } from "../Confirm/actions";
import { SettingsActions } from "src/redux/Settings/actions";
import {
    SettingsSelectors,
    SelectPeerCptySetting,
} from "src/redux/Settings/selectors";
import { CounterPartysActions } from "src/redux/CounterPartys/actions";

import {
    INTERMEDIARY_SELECT_TYPES,
    TX_TYPE_VALUES,
} from "src/redux/Confirmations/constants";
import { WebWorkerActions } from "../WebWorker/actions";

const buildDraftOffer = (state, borrower, tx) => {
    const { parentEntry, index } = TxSelectors.GetChildEntries(state, tx.id);
    return index > 0
        ? {
              ...tx,
              id: tx.id + "#" + index,
              index: index,
              preloanId: tx.id,
              needId: tx.id, //CHK: Do we need this?
              myRole: "LENDER",
              borrower,
              requestedEconomics: {},
              offeredEconomics: {
                  callable: tx.callable,
                  collateralType: tx.collateralType || {
                      type: null,
                  },
                  dividend: tx.dividend,
                  fee: tx.fee,
                  maturityDate: tx.maturityDate,
                  quantity: tx.quantity,
                  rateRebate: tx.rateRebate,
                  valueDate: tx.valueDate,
                  txType: tx.txType || TX_TYPE_VALUES.ANY,
                  intermediary:
                      tx.intermediary || INTERMEDIARY_SELECT_TYPES.ANY,
              },
              state: TX_STATES.OFFERING,
              lastUpdated: parentEntry.lastUpdated,
          }
        : null;
};

const buildNewOffers = (rowId, parentEntry, childEntries, index) => {
    // Every new split child copies the parent's offeredEconomics so
    // structural fields (collateral, valueDate, maturity, callable,
    // dividend, intermediary) are present for downstream validation
    // in isTxDataRowValid. The first child of a fresh split keeps the
    // parent's quantity and rate as a starting point; additional
    // children clear quantity and fee/rateRebate so the grid shows
    // them as empty and the user must fill them in before Approve is
    // enabled. Ports oldest-sources/truweb-ui 62f71170 + c7b0efac —
    // before this, only the first child got the parent's economics;
    // siblings inherited `offeredEconomics: {}` and were silently
    // dropped by selectTxEntriesToProceed because
    // isQuantityValid(undefined) is false. The user saw 3 rows in the
    // grid but only one made it into the SendResponseOffer txSet.
    const makeChild = (childIndex, clearQtyAndRate) => {
        const economics = { ...parentEntry.offeredEconomics };
        if (clearQtyAndRate) {
            delete economics.quantity;
            delete economics.fee;
            delete economics.rateRebate;
        }
        return {
            ...parentEntry,
            state: TX_STATES.OFFERING,
            preloanId: rowId,
            index: childIndex,
            offeredEconomics: economics,
            id: rowId + "#" + childIndex,
        };
    };

    if (childEntries?.length === 0) {
        return [makeChild(index, false), makeChild(index + 1, true)];
    } else {
        return [makeChild(index, true)];
    }
};

const normalizeRate = (rate) => {
    let rateBp = Validators.parseFloatToSizeLimit(rate);
    if (isNumber(rateBp)) {
        rateBp = Math.round(rateBp * 100);
    }
    return rateBp;
};

const prepareRowData = (
    rowData,
    collateralType,
    requestSettings,
    state,
    transactionIds,
    enablePurpose,
    isPortfolioRequest = false
) => {
    let rowsToProcess = rowData;
    if (isPortfolioRequest) {
        rowsToProcess = rowData.filter((row) => !row.parentId);
    }
    return rowsToProcess.map((row, index) => {
        let rowOutput = {
            id:
                transactionIds && transactionIds[index] !== null
                    ? transactionIds[index]
                    : null,
            quantity: row.Quantity,
            security: row.marketData?.data,
            valueDate: setDateFormat(row.ValueDate, "-"),
            txType: requestSettings.txType,
            type: requestSettings.loanType || LOAN_TYPES.LOAN,
            callable: empty(requestSettings.callable)
                ? null
                : requestSettings.callable,
            intermediary: requestSettings.intermediary,
            maturityDate: setDateFormat(row.Maturity, "-") || "9999-12-31",
            purpose: enablePurpose ? row.Purpose : undefined,
        };

        if (
            collateralType.cash ||
            collateralType.nonCash ||
            collateralType.type
        ) {
            rowOutput.collateralType = collateralType;
        }

        if (row.CalculatedRate) {
            if (row.FR === "R") {
                rowOutput.rateRebate = {
                    rateBp: Math.round(row.CalculatedRate * 100),
                };
            } else {
                rowOutput.fee = {
                    rateBp: Math.round(row.CalculatedRate * 100),
                };
            }
        }

        if (!empty(row.Dividend)) rowOutput.dividend = row.Dividend;
        rowOutput.lastUser = AuthSelectors.User(state).attributes.email;

        if (isPortfolioRequest) {
            rowOutput.removedConstituents = rowData
                .filter((r) => r.parentId === row.SecurityId && r.isRemoved)
                // send isin of the fetched market data for known constituents
                // for unknown constituents (no market data), send seurity id pasted by the user
                .map((rc) => ({
                    isin: rc.marketData?.data?.isin ?? rc.SecurityId.trim(),
                }));
        }

        return rowOutput;
    });
};

const prepareSendInitiateRequest = async (
    state,
    createRowData,
    selectedCptys,
    requestSettings
) => {
    // Prepare acceptance criteria
    let ac = getFromLocalUserStorage(Tx.AC_STORAGE_VALUE);
    const { includesAC } = getFromLocalUserStorage(Tx.AC_STORAGE_TOGGLE);

    if (includesAC) {
        ac.preferredType = requestSettings.loanType || LOAN_TYPES.LOAN;
        const disables = TxSelectors.TxInitDisables(state);
        if (disables.includes("maxRateBps")) {
            ac.maxRateBps = "";
        }
    }

    // Prepare internal fund code
    const { value: fundName } = getFromLocalUserStorage(
        Tx.FUND_CODE_STORAGE_VALUE
    );

    // Prepare request
    const url = AuthSelectors.BuildTxBorrowInitUrl(state);
    const comment = TxSelectors.TxInitOfferComment(state);

    const request = {
        txSet: createRowData,
        user: {
            email: AuthSelectors.User(state).attributes.email,
        },
        comment,
        lenders: selectedCptys,
        acceptanceCriteria: includesAC ? ac : null,
        fundName,
    };

    return {
        request,
        apiUrl: url,
    };
};

const makeStartBatchAPICall = (dispatch, lender, url) => {
    return dispatch(
        fetchWrapper(url, {
            method: "POST",
            body: JSON.stringify(lender),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        })
    );
};

const appendConstituentDataToTxInitRows = (parentRow, data) => {
    let result = [parentRow];
    if (data.constituents && Array.isArray(data.constituents)) {
        data.constituents.forEach((constituent) => {
            result.push({
                SecurityId: constituent.constituent,
                Quantity: constituent.cu_value,
                CuValue: constituent.cu_value,
                parentId: parentRow.SecurityId, // for ag-grid grouping
                isRemoved: false,
            });
        });
    }
    return result;
};

export const TxActions = {
    /**
     * TX_LIST
     */
    SetHideInactive: (hideInactive) => ({
        type: Tx.SET_HIDE_INACTIVE,
        hideInactive,
    }),

    UpdateEntries: () => {
        return (dispatch, getState) => {
            const role = TopBarSelectors.Role(getState());
            dispatch(TxActions.ResetOfferingTxs());
            if (role === ROLE.BORROWER) {
                dispatch(SettingsActions.GetBorrowerSettings());
                return dispatch(TxActions.UpdateBorrowEntries());
            } else if (role === ROLE.LENDER) {
                dispatch(InventoryActions.GetTodayInventory());
                return dispatch(TxActions.UpdateLendEntries());
            }
        };
    },

    UpdateEntry: (rows) => {
        return (dispatch, getState) => {
            const role = TopBarSelectors.Role(getState());
            const entries = rows.map((rowData) => {
                const currEntries = TxSelectors.TxRawEntries(getState());
                let entry = currEntries.find(
                    (entry) => entry.id === rowData.id
                );

                const rateBpOffer = normalizeRate(rowData.OfferRate);
                const rateBpReq = normalizeRate(rowData.ReqRate);

                var res = {
                    ...entry,
                    offeredEconomics: {
                        ...entry.offeredEconomics,
                        quantity: Validators.parseIntToSizeLimit(
                            rowData.OfferQuantity
                        ),
                        fee: { rateBp: rateBpOffer },
                        rateRebate: { rateBp: rateBpOffer },
                        callable: rowData.Callable,
                        valueDate: setDateFormat(rowData.ValueDate, "-"),
                        maturityDate:
                            setDateFormat(rowData.Maturity, "-") ||
                            "9999-12-31",
                        collateralType: {
                            type: COLLAT_TYPES[rowData.CollateralType] || null,
                            cash: {},
                            nonCash: {
                                schedule: rowData.CollateralCCY,
                            },
                            ccy: rowData.CollateralCCY
                                ? [rowData.CollateralCCY]
                                : [],
                        },
                        dividend: Validators.parseFloatToSizeLimit(
                            rowData.Dividend
                        ),
                        txType: rowData.TxType,
                        intermediary: rowData.Intermediary,
                    },
                    requestedEconomics: {
                        ...entry.requestedEconomics,
                        quantity: Validators.parseIntToSizeLimit(
                            rowData.ReqQuantity
                        ),
                        fee: { rateBp: rateBpReq },
                        rateRebate: { rateBp: rateBpReq },
                    },
                    newType: rowData.NewType,
                    objective: rowData.Objective,
                    listName: rowData.ListName,
                    prevListName:
                        entry.listName !== rowData.ListName
                            ? entry.listName
                            : entry.prevListName,
                    fundName:
                        rowData.FundName === "None" ? null : rowData.FundName,
                    state: rowData.Status,
                    fr: rowData.FR,
                    purpose: rowData.Purpose,
                };
                return res;
            });

            if (role === ROLE.BORROWER) {
                dispatch(TxActions.UpdateBorrowListUiEdit(entries));
            } else if (role === ROLE.LENDER) {
                dispatch(TxActions.UpdateLendListUiEdit(entries));
            }
        };
    },

    UpdateBorrowEntries: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            return dispatch(
                fetchWrapper(AuthSelectors.BuildTxBorrowListUrl(getState()))
            )
                .then((result) => {
                    dispatch(SpinnerActions.Close());
                    dispatch({
                        type: Tx.UPDATE_BORROW_LIST,
                        txEntries: result,
                    });
                })
                .catch((error) => {
                    dispatch(SpinnerActions.Close());
                    if (error?.message !== "Unauthorized Error") {
                        dispatch(
                            ToasterActions.NewError(
                                "Error at Fetching Borrower TX entries"
                            )
                        );
                    }
                });
        };
    },

    UpdateLendEntries: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            return dispatch(
                fetchWrapper(AuthSelectors.BuildTxLenderListUrl(getState()))
            )
                .then((result) => {
                    dispatch(SpinnerActions.Close());
                    const txEntries = result.map((lte) => {
                        let isPartner = CounterPartysSelectors.IsPartnerCpty(
                            getState(),
                            lte.borrower.id
                        );

                        let reqCallable = lte.requestedEconomics?.callable;
                        if (empty(reqCallable)) reqCallable = false;
                        let reqDividend = lte.requestedEconomics?.dividend;

                        return {
                            ...lte,
                            // set defaults
                            offeredEconomics: lte.offeredEconomics || {
                                dividend: isPartner
                                    ? reqDividend
                                    : reqDividend ?? 100,
                                callable: reqCallable,
                            },
                        };
                    });
                    dispatch({
                        type: Tx.UPDATE_LEND_LIST,
                        txEntries: txEntries,
                    });
                })
                .catch((error) => {
                    dispatch(SpinnerActions.Close());
                    if (error?.message !== "Unauthorized Error") {
                        dispatch(
                            ToasterActions.NewError(
                                "Error at Fetching Lender TX entries"
                            )
                        );
                    }
                });
        };
    },

    /**
     * TX_RESPOND
     */
    SetRespondComment: (comment) => {
        return {
            type: Tx.SET_RESPOND_COMMENT,
            comment,
        };
    },

    SendResponseLenderReject: () => {
        return (dispatch, getState) => {
            let responseTxIds = TxSelectors.TxEntriesToReject(getState()).map(
                (e) => e.id
            );
            if (responseTxIds.length > 0) {
                const rejectRequest = {
                    txIds: responseTxIds,
                    comment: TxSelectors.TxRespondComment(getState()),
                };
                return dispatch(
                    fetchWrapper(
                        AuthSelectors.BuildTxLenderReject(getState()),
                        {
                            method: "POST",
                            body: JSON.stringify(rejectRequest),
                            headers: {
                                Accept: "application/json",
                                "Content-Type": "application/json",
                            },
                        }
                    )
                )
                    .then((res) => {
                        dispatch({
                            type: Tx.RESPOND_SENT,
                            correlationId: res.correlationId,
                        });
                        return dispatch({
                            type: Tx.UPDATE_TX_STATE_LIST,
                            txEntries: responseTxIds.map((id) => ({
                                state: TX_STATES.REJECTED,
                                domainId: id,
                                correlationId: res.correlationId,
                                comment: rejectRequest.comment,
                            })),
                        });
                    })
                    .catch((error) => {
                        dispatch({
                            type: Tx.RESPOND_ERROR,
                            errorMessage: error,
                        });
                    });
            }
            return Promise.resolve();
        };
    },

    SendResponseBorrowerReject: () => {
        return (dispatch, getState) => {
            let responseTxIds = TxSelectors.TxEntriesToReject(getState()).map(
                (e) => e.id
            );
            if (responseTxIds.length > 0) {
                const rejectRequest = {
                    txIds: responseTxIds,
                };
                return dispatch(
                    fetchWrapper(
                        AuthSelectors.BuildTxBorrowReject(getState()),
                        {
                            method: "POST",
                            body: JSON.stringify(rejectRequest),
                            headers: {
                                Accept: "application/json",
                                "Content-Type": "application/json",
                            },
                        }
                    )
                )
                    .then((res) => {
                        dispatch({
                            type: Tx.RESPOND_SENT,
                            correlationId: res.correlationId,
                        });
                        return dispatch({
                            type: Tx.UPDATE_TX_STATE_LIST,
                            txEntries: responseTxIds.map((id) => ({
                                state: TX_STATES.REJECTED,
                                domainId: id,
                                correlationId: res.correlationId,
                            })),
                        });
                    })
                    .catch((error) => {
                        dispatch({
                            type: Tx.RESPOND_ERROR,
                            errorMessage: error,
                        });
                    });
            }
            return Promise.resolve();
        };
    },

    SendResponseLoanRequest: () => {
        return (dispatch, getState) => {
            const state = getState();
            const entriesToAccept = TxSelectors.TxEntriesToAccept(state);
            if (entriesToAccept.length <= 0) return Promise.resolve();
            const txSet = entriesToAccept.map((e) => {
                return {
                    id: e.id,
                    fundName: e.fundName || null,
                    purpose: e.purpose,
                };
            });
            return dispatch(
                fetchWrapper(AuthSelectors.BuildTxAcceptOffer(state), {
                    method: "POST",
                    body: JSON.stringify({ txSet }),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            )
                .then((res) => {
                    dispatch({
                        type: Tx.RESPOND_SENT,
                        correlationId: res.correlationId,
                    });
                    return dispatch({
                        type: Tx.UPDATE_TX_STATE_LIST,
                        txEntries: txSet.map((tx) => ({
                            state: TX_STATES.LOAN_REQUESTED,
                            domainId: tx.id,
                            correlationId: res.correlationId,
                        })),
                    });
                })
                .catch((error) => {
                    dispatch({
                        type: Tx.RESPOND_ERROR,
                        errorMessage: error,
                    });
                });
        };
    },

    SendResponseUpgrade: () => {
        return (dispatch, getState) => {
            const responseTxSet = TxSelectors.TxEntriesToUpgrade(
                getState()
            ).map((e) => ({
                id: e.id,
                quantity: e.requestedEconomics.quantity,
                fundName: e.fundName,
                fee:
                    e.state === TX_STATES.FORKED
                        ? e.offeredEconomics.fee
                        : e.requestedEconomics.fee,
            }));
            if (responseTxSet.length > 0) {
                return dispatch(
                    fetchWrapper(
                        AuthSelectors.BuildTxBorrowUpgrade(getState()),
                        {
                            method: "POST",
                            body: JSON.stringify({
                                txSet: responseTxSet,
                            }),
                            headers: {
                                Accept: "application/json",
                                "Content-Type": "application/json",
                            },
                        }
                    )
                )
                    .then((res) => {
                        dispatch({
                            type: Tx.RESPOND_SENT,
                            correlationId: res.correlationId,
                        });

                        return dispatch({
                            type: Tx.UPDATE_TX_STATE_LIST,
                            txEntries: responseTxSet.map((tx) => ({
                                state: TX_STATES.UPGRADE_REQUESTED,
                                domainId: tx.id,
                                correlationId: res.correlationId,
                                expiresAt: undefined,
                            })),
                        });
                    })
                    .catch((error) => {
                        return dispatch({
                            type: Tx.RESPOND_ERROR,
                            errorMessage: error,
                        });
                    });
            }
            return Promise.resolve();
        };
    },
    SendResponseRelease: () => {
        return (dispatch, getState) => {
            const responseTxIds = TxSelectors.TxEntriesToRelease(
                getState()
            ).map((e) => e.id);

            if (responseTxIds.length > 0) {
                return dispatch(
                    fetchWrapper(
                        AuthSelectors.BuildTxBorrowRelease(getState()),
                        {
                            method: "POST",
                            body: JSON.stringify({
                                txIds: responseTxIds,
                            }),
                            headers: {
                                Accept: "application/json",
                                "Content-Type": "application/json",
                            },
                        }
                    )
                )
                    .then((res) => {
                        return dispatch({
                            type: Tx.RESPOND_SENT,
                            correlationId: res.correlationId,
                        });
                    })
                    .catch((error) => {
                        return dispatch({
                            type: Tx.RESPOND_ERROR,
                            errorMessage: error,
                        });
                    });
            }
            return Promise.resolve();
        };
    },

    SendResponseFork: () => {
        return (dispatch, getState) => {
            const uniqueBorrowers = [
                ...new Set(
                    TxSelectors.TxEntriesToFork(getState()).map(
                        (entry) => entry.borrower.id
                    )
                ),
            ];

            if (uniqueBorrowers.length > 0) {
                return Promise.all(
                    uniqueBorrowers.map((borrowerId) => {
                        const txEntries = TxSelectors.TxEntriesToFork(
                            getState()
                        ).filter((tx) => tx.borrower.id === borrowerId);
                        const offerRequest = {
                            user: {
                                email: AuthSelectors.User(getState()).attributes
                                    .email,
                            },
                            borrower: {
                                id: borrowerId,
                            },
                            txSet: txEntries.map((tx) => {
                                return {
                                    id: tx.id,
                                    fee: tx.offeredEconomics.fee,
                                };
                            }),
                        };

                        return dispatch(
                            fetchWrapper(
                                AuthSelectors.BuildTxLenderFork(getState()),
                                {
                                    method: "POST",
                                    body: JSON.stringify(offerRequest),
                                    headers: {
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    },
                                }
                            )
                        )
                            .then((res) => {
                                return dispatch({
                                    type: Tx.RESPOND_SENT,
                                    correlationId: res.correlationId,
                                });
                            })
                            .catch((error) => {
                                dispatch({
                                    type: Tx.RESPOND_ERROR,
                                    errorMessage: error,
                                });
                            });
                    })
                );
            }
            return Promise.resolve();
        };
    },

    SendResponseOffer: () => {
        return (dispatch, getState) => {
            const state = getState();
            const uniqueBorrowers = [
                ...new Set(
                    TxSelectors.TxEntriesToAccept(state).map(
                        (entry) => entry.borrower.id
                    )
                ),
            ];

            if (uniqueBorrowers.length > 0) {
                const fetchPromises = Promise.all(
                    uniqueBorrowers.map((borrowerId) => {
                        let txEntries = TxSelectors.TxEntriesToAccept(
                            state
                        ).filter((tx) => tx.borrower.id === borrowerId);

                        const offerRequest = {
                            user: {
                                email: AuthSelectors.User(state).attributes
                                    .email,
                            },
                            borrower: {
                                id: borrowerId,
                            },
                            comment: TxSelectors.TxRespondComment(state),
                            txSet: txEntries.map((tx) => ({
                                preloanId:
                                    tx.state === TX_STATES.OFFERING
                                        ? tx.preloanId
                                        : tx.id,
                                type: tx.newType,
                                objective: tx.objective,
                                security: tx.security,
                                quantity: tx.offeredEconomics.quantity,
                                collateralType: {
                                    type: tx.offeredEconomics.collateralType
                                        .type,
                                    ccy: tx.offeredEconomics.collateralType.ccy,
                                },
                                fee: tx.offeredEconomics.fee,
                                valueDate: setDateFormat(
                                    tx.offeredEconomics.valueDate,
                                    "-"
                                ),
                                maturityDate:
                                    setDateFormat(
                                        tx.offeredEconomics.maturityDate,
                                        "-"
                                    ) || "9999-12-31",
                                callable: tx.offeredEconomics.callable,
                                dividend: tx.offeredEconomics.dividend,
                                rateRebate: tx.offeredEconomics.rateRebate,
                                txType: tx.offeredEconomics.txType,
                                intermediary: tx.offeredEconomics.intermediary,
                                listName: tx.listName,
                            })),
                        };
                        return dispatch(
                            fetchWrapper(
                                AuthSelectors.BuildTxLenderOfferUrl(state),
                                {
                                    method: "POST",
                                    body: JSON.stringify(offerRequest),
                                    headers: {
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    },
                                }
                            )
                        )
                            .then((res) => {
                                return dispatch({
                                    type: Tx.RESPOND_SENT,
                                    correlationId: res.correlationId,
                                    status: TX_STATES.OFFERED,
                                });
                            })
                            .catch((error) => {
                                dispatch({
                                    type: Tx.RESPOND_ERROR,
                                    errorMessage: error,
                                });
                            });
                    })
                );

                dispatch({ type: Tx.SEND_RESPOND });
                return fetchPromises.then(() => {
                    //Remove all offering txs
                    dispatch(
                        TxActions.UpdateLendListUiEdit(
                            TxSelectors.TxRawEntries(state)
                                .filter((tx) => tx.state === TX_STATES.OFFERING)
                                .map((e) => {
                                    e.state = TX_STATES.SENDING;
                                    return e;
                                })
                        )
                    );
                });
            }
            return Promise.resolve();
        };
    },

    SendRespondApproveUpgradeRequest: () => {
        return (dispatch, getState) => {
            const entries = TxSelectors.TxEntriesToAcceptUpgrade(getState());

            if (entries.length) {
                return Promise.all(
                    entries.map((entry) => {
                        const payload = {
                            rate: entry.offeredEconomics.fee?.rateBp,
                            quantity: entry.offeredEconomics.quantity,
                        };

                        return dispatch(
                            fetchWrapper(
                                AuthSelectors.BuildEAUpgradeRequestApproveUrl(
                                    getState(),
                                    entry.id
                                ),
                                {
                                    method: "POST",
                                    body: JSON.stringify(payload),
                                    headers: {
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    },
                                }
                            )
                        )
                            .then((res) => {
                                dispatch({
                                    type: Tx.RESPOND_SENT,
                                    correlationId: res.correlationId,
                                });

                                return dispatch({
                                    type: Tx.UPDATE_TX_STATE_LIST,
                                    txEntries: entries.map((e) => {
                                        return {
                                            ...e,
                                            state: TX_STATES.CONFIRMED,
                                            type: LOAN_TYPES.LOAN,
                                        };
                                    }),
                                });
                            })
                            .catch((error) => {
                                dispatch({
                                    type: Tx.RESPOND_ERROR,
                                    errorMessage: error,
                                });
                            });
                    })
                );
            }

            return Promise.resolve();
        };
    },

    SendRespondRejectUpgradeRequest: () => {
        return (dispatch, getState) => {
            const entries = TxSelectors.TxEntriesToAcceptUpgrade(getState());

            if (entries.length) {
                return Promise.all(
                    entries.map((entry) => {
                        return dispatch(
                            fetchWrapper(
                                AuthSelectors.BuildEAUpgradeRequestRejectUrl(
                                    getState(),
                                    entry.id
                                ),
                                {
                                    method: "POST",
                                    headers: {
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    },
                                }
                            )
                        )
                            .then((res) => {
                                dispatch({
                                    type: Tx.RESPOND_SENT,
                                    correlationId: res.correlationId,
                                });

                                return dispatch({
                                    type: Tx.UPDATE_TX_STATE_LIST,
                                    txEntries: entries.map((e) => {
                                        return {
                                            ...e,
                                            domainId: e.id,
                                            correlationId: res.correlationId,
                                            state: TX_STATES.REJECTED,
                                        };
                                    }),
                                });
                            })
                            .catch((error) => {
                                dispatch({
                                    type: Tx.RESPOND_ERROR,
                                    errorMessage: error,
                                });
                            });
                    })
                );
            }

            return Promise.resolve();
        };
    },

    /**
     * TX_INITIATE
     */
    UpdateInitRow: (rows) => {
        const { includesAC } = getFromLocalUserStorage(Tx.AC_STORAGE_TOGGLE);
        const ac = getFromLocalUserStorage(Tx.AC_STORAGE_VALUE);
        return (dispatch) => {
            dispatch({
                type: Tx.UPDATE_INIT_ROW,
                rows,
            });
            dispatch(TxActions.UpdateAC(includesAC, ac));
        };
    },

    DeleteInitRow: (rowId) => {
        return {
            type: Tx.DELETE_INIT_ROW,
            rowId,
        };
    },

    ResetInit: () => {
        return {
            type: Tx.RESET_INIT,
        };
    },

    PrepareInit: (rowData) => {
        return async (dispatch, getState) => {
            const state = getState();
            const inventory = InventorySelectors.GetTodayInventory(state);
            const role = TopBarSelectors.Role(state);

            if (role === ROLE.LENDER) {
                dispatch({
                    type: Tx.PREPARE_INIT,
                    rowData: rowData.map((data) => {
                        const list = SettingsSelectors.MatchedListForNewOffer(
                            state,
                            data.ListName
                        );

                        const listDetails = {
                            TxType: list?.txType,
                            Callable: list?.callable,
                            Dividend: list?.dividend,
                            CollateralType: list?.collType ?? "NC",
                            CollateralCCY: list?.collCcy,
                        };
                        return {
                            ...data,
                            ...listDetails,
                            // used for resetting offer details to list details
                            originListDetails: {
                                ...listDetails,
                            },
                            marketData: {
                                status: FETCH_STATES.FETCHING,
                            },
                            FR: listDetails.CollateralType === "CA" ? "R" : "F",
                        };
                    }),
                });
            } else {
                dispatch({
                    type: Tx.PREPARE_INIT,
                    rowData: rowData.map((row) => ({
                        ...row,
                        marketData: {
                            status: FETCH_STATES.FETCHING,
                        },
                    })),
                });
            }
            await dispatch(TxActions.GetMarketData(rowData));
            role === ROLE.LENDER &&
                dispatch({
                    type: Tx.ATTACH_INVENTORY,
                    inventory,
                });
        };
    },

    PrepareTxUploads: (rowData) => {
        return async (dispatch, getState) => {
            await dispatch(SpinnerActions.Open());

            const role = TopBarSelectors.Role(getState());
            await dispatch(TxActions.PrepareInit(rowData));

            // check availability first
            if (role === ROLE.BORROWER) {
                // covers the case where users haven't visited availability tab yet
                await dispatch(SettingsActions.GetBorrowerSettings());
                await dispatch(AvailabilityActions.GetTodayAvailability());
                // to show custom lenders
                await dispatch(CounterPartysActions.GetAll());
                const enable90DayMaxMaturity =
                    getState().Settings?.featureFlags[
                        "enable-90-day-max-maturity-date"
                    ];
                await dispatch(
                    AvailabilityActions.PrepareNeeds(
                        rowData,
                        true,
                        enable90DayMaxMaturity
                    )
                );
                const requestRows = AvailabilitySelectors.GetRequestRows(
                    getState()
                );

                // Limited UI: requirements §3 "Available Matches Disabled".
                // The Available Matches popup is the borrower's first-class
                // pre-trade screen — under limited-ui it's hidden and the
                // borrower goes straight to the Preview flow.
                // Backend behaviour is unchanged (matches are still
                // computed; we just don't surface them).
                const limitedUi =
                    getState().Settings?.featureFlags?.["limited-ui"] === true;
                if (requestRows.length > 0 && !limitedUi) {
                    // drey: todo
                    const openNeedScreen = async () => {
                        await dispatch(AvailabilityActions.ResetTake());
                        await dispatch(
                            ModalActions.Open(UploadNeedsPreview, {
                                title: "Preview",
                                large: true,
                                padding0: true,
                                ignoreClickOutside: true,
                                onClose: () => {
                                    dispatch(TxActions.ResetInit());
                                    dispatch(ModalActions.CloseAll());
                                },
                            })
                        );
                    };

                    await dispatch(
                        ModalActions.Open(SearchAvailabilityPreview, {
                            title: "Available Matches",
                            large: true,
                            padding0: true,
                            ignoreClickOutside: true,
                            customProps: {
                                openAdvertise: true,
                                onCancel: () => {
                                    dispatch(ModalActions.Close());
                                    dispatch(ModalActions.CloseAll());
                                    openNeedScreen();
                                },
                            },
                            afterClose: () => {
                                openNeedScreen();
                            },
                        })
                    );
                    return await dispatch(SpinnerActions.Close());
                }
            }
            // open UploadNeedsPreview
            await dispatch(
                ModalActions.Open(UploadNeedsPreview, {
                    title:
                        role === ROLE.BORROWER
                            ? "Preview"
                            : "Preview Create Offer",
                    large: true,
                    padding0: true,
                    onClose: () => {
                        dispatch(TxActions.ResetInit());
                        dispatch(ModalActions.CloseAll());
                    },
                })
            );
            await dispatch(SpinnerActions.Close());
        };
    },

    PreparePortfolioTxUploads: (rowData) => async (dispatch, getState) => {
        try {
            await dispatch(SpinnerActions.Open());
            const parentRow = { ...rowData[0] };

            // First query the exhaustive list of security codes
            const portfolioMarketData = await dispatch(
                fetchWrapper(
                    AuthSelectors.BuildMarketDataUrl(
                        getState(),
                        parentRow.SecurityId
                    )
                )
            );

            // Then use Koscom compliant ticker to get portfolio details
            const portfolioDetails = await dispatch(
                fetchWrapper(
                    AuthSelectors.BuildMarketDataPortfolioUrl(
                        getState(),
                        portfolioMarketData.data.ticker
                    )
                )
            );

            const txInitRows = appendConstituentDataToTxInitRows(
                parentRow,
                portfolioDetails
            );
            await dispatch(TxActions.PrepareInit(txInitRows));

            await dispatch(
                ModalActions.Open(UploadNeedsPreview, {
                    title: "Locate Portfolio",
                    large: true,
                    padding0: true,
                    onClose: () => {
                        dispatch(TxActions.ResetInit());
                        dispatch(ModalActions.CloseAll());
                    },
                })
            );
        } finally {
            await dispatch(SpinnerActions.Close());
        }
    },

    UpdateAC: (includesAC, ac, target) => {
        return (dispatch, getState) => {
            const state = getState();
            let rowData;
            if (target === VIEWS.AVAILABILITY) {
                rowData = AvailabilitySelectors.GetRequestRows(state);
            } else {
                rowData = TxSelectors.TxCreateRowData(state);
            }
            return dispatch({
                type: Tx.UPDATE_AC,
                includesAC,
                ac,
                rowData,
            });
        };
    },

    SetOfferComment: (comment) => {
        return {
            type: Tx.SET_OFFER_COMMENT,
            comment,
        };
    },

    SetOfferSettings: (settings) => {
        return {
            type: Tx.SET_OFFER_SETTINGS,
            settings,
        };
    },

    ApplySendOfferDetails: (details) => {
        return {
            type: Tx.APPLY_SEND_OFFER_DETAILS,
            details,
        };
    },

    ApplyDefaultObjective: (cptyId) => (dispatch, getState) => {
        const offerSettings = TxSelectors.TxInitOfferSettings(getState());
        const peerCptySetting = SelectPeerCptySetting(getState(), cptyId);

        dispatch({
            type: Tx.SET_OFFER_SETTINGS,
            settings: {
                ...offerSettings,
                objective: peerCptySetting?.defaultObjective,
            },
        });
    },

    SetRequestSettings: (settings) => {
        return {
            type: Tx.SET_REQUEST_SETTINGS,
            settings,
        };
    },

    GetMarketData: (rowData) => {
        return async (dispatch, getState) => {
            const listSecurityId = rowData.map((row) => row.SecurityId);
            const role = TopBarSelectors.Role(getState());
            // get data from storage
            return dispatch(
                WebWorkerActions.getStockData({
                    cmd: "start",
                    listSecurityId: listSecurityId,
                    role: role,
                    apiUrl: AuthSelectors.MarketDataBatchUrl(getState()),
                    isInventoryStock: false,
                })
            );
        };
    },

    // TODO: Use MD action instead
    EditMarketData: (rowId, stockSymbol) => {
        return (dispatch, getState) => {
            return dispatch(
                fetchWrapper(
                    AuthSelectors.BuildMarketDataUrl(getState(), stockSymbol)
                )
            )
                .then((result) => {
                    dispatch({
                        type: Tx.EDIT_MARKET_DATA,
                        result,
                        stockSymbol,
                    });
                })
                .catch((error) => {
                    dispatch({
                        type: Tx.REPORT_MD_ERROR,
                        rowId,
                        error: error.message,
                    });
                });
        };
    },

    SelectOneAndOnlyCpty: (cpty) => {
        return (dispatch, getState) => {
            const selectedCptys = TxSelectors.TxInitSelectedCptyIds(getState());

            // Store the id, not the whole cpty object — keeps the state
            // shape consistent with SelectOneCpty / SelectAllCptys (both
            // store id strings). Storing objects here meant downstream
            // selectors that resolve id->object via CounterPartysSelectors.Get
            // (e.g. TxInitSelectedCptys) returned `undefined` for the
            // object-shaped entries, which crashed RequestDefaults's
            // `cpty.unregistered` check the moment the borrower picked a
            // cpty under limited-ui (TypeError: can't access property
            // "unregistered", e is undefined).
            if (selectedCptys[0] !== cpty.id) {
                dispatch({
                    type: Tx.SET_INITIATE_CPTY,
                    selectedCptys: [cpty.id],
                });
            }
        };
    },

    SelectOneCpty: (cptyId) => {
        return (dispatch, getState) => {
            const selectedCptys = TxSelectors.TxInitSelectedCptyIds(getState());

            let newCptyList;
            if (selectedCptys.includes(cptyId)) {
                newCptyList = selectedCptys.filter((cpty) => cpty !== cptyId);
            } else {
                newCptyList = [...selectedCptys, cptyId];
            }

            dispatch({
                type: Tx.SET_INITIATE_CPTY,
                selectedCptys: newCptyList,
                cptyError: empty(newCptyList),
            });
        };
    },

    SelectAllCptys: () => {
        return (dispatch, getState) => {
            const knownCptys = CounterPartysSelectors.SelectAll(getState());
            const prevSelectedCptys = TxSelectors.TxInitSelectedCptyIds(
                getState()
            );
            const loanType = TxSelectors.TxInitSelectedLoanType(getState());

            let newCptyList = [];
            // only update list if some cptys were still not selected
            if (prevSelectedCptys.length < knownCptys.length) {
                newCptyList = knownCptys
                    .filter((cpty) => loanType !== "HOLD" || !cpty.unregistered)
                    .map((cpty) => cpty.id);
            }

            dispatch({
                type: Tx.SET_INITIATE_CPTY,
                selectedCptys: newCptyList,
                cptyError: empty(newCptyList),
            });
        };
    },

    SendPortfolioInitiate: () => async (dispatch, getState) => {
        dispatch(SpinnerActions.Open());
        const state = getState();
        const enablePurpose = state.Settings?.featureFlags["enable-purpose"];

        dispatch({ type: Tx.SEND_INIT });

        // Prepare counterparty (lender)
        const selectedCptys = TxSelectors.TxInitSelectedCptys(state);
        if (empty(selectedCptys)) {
            dispatch({ type: Tx.SET_CPTY_ERROR, value: true });
            dispatch({ type: Tx.CANCEL_SEND });
            dispatch(SpinnerActions.Close());
            return Promise.resolve();
        } else {
            dispatch({ type: Tx.SET_CPTY_ERROR, value: false });
        }

        // Prepare request
        const requestSettings = TxSelectors.TxInitRequestSettings(state);

        let collateralType = { ...requestSettings.collateralType };
        if (empty(collateralType.type)) collateralType.type = null;
        if (
            empty(collateralType.ccy) ||
            collateralType.type === COLLAT_TYPES.NC
        )
            delete collateralType.cash;
        if (empty(collateralType.nonCash.schedule))
            delete collateralType.nonCash;

        const createRowData = prepareRowData(
            TxSelectors.TxInitRowData(state),
            collateralType,
            requestSettings,
            state,
            undefined,
            enablePurpose,
            true
        );

        const { value: fundName } = getFromLocalUserStorage(
            Tx.FUND_CODE_STORAGE_VALUE
        );
        const comment = TxSelectors.TxInitOfferComment(state);
        const requestBody = {
            ...createRowData[0],
            user: {
                email: AuthSelectors.User(state).attributes.email,
            },
            lenders: selectedCptys,
            fundName,
            comment,
        };

        return dispatch(
            fetchWrapper(AuthSelectors.BuildTxPortfolioInitUrl(state), {
                method: "POST",
                body: JSON.stringify(requestBody),
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
        )
            .then((res) => {
                dispatch({
                    type: Tx.INIT_SENT,
                    correlationId: res.correlationId,
                });
                dispatch({
                    type: Tx.INSERT_BORROW_TX_LIST,
                    txSet: res.txSet.map((tx) => ({
                        ...tx,
                        state: TX_STATES.PRE_LOAN_REQUESTED,
                    })),
                });
                dispatch({ type: Tx.RESET_INIT });
                dispatch(SpinnerActions.Close());
                dispatch(ModalActions.CloseAll());
            })
            .catch((error) => {
                dispatch(SpinnerActions.Close());
                dispatch({ type: Tx.INIT_ERROR, error });
            });
    },

    SendInitiate: () => {
        return async (dispatch, getState) => {
            let createRowData;
            const state = getState();
            const enableBatching =
                state.Settings?.featureFlags["enable-batching"];
            const enablePurpose =
                state.Settings?.featureFlags["enable-purpose"];

            dispatch(SpinnerActions.Open());
            dispatch({ type: Tx.SEND_INIT });

            if (!TxSelectors.TxInitIsReady(state)) {
                dispatch({ type: Tx.CANCEL_SEND });
                dispatch(ModalActions.Close());
                dispatch(SpinnerActions.Close());
                return Promise.resolve();
            }

            // Prepare counterparty (lender)
            const selectedCptys = TxSelectors.TxInitSelectedCptys(state);
            if (empty(selectedCptys)) {
                dispatch({ type: Tx.SET_CPTY_ERROR, value: true });
                dispatch({ type: Tx.CANCEL_SEND });
                dispatch(SpinnerActions.Close());
                return Promise.resolve();
            } else {
                dispatch({ type: Tx.SET_CPTY_ERROR, value: false });
            }

            // Prepare TX set
            const requestSettings = TxSelectors.TxInitRequestSettings(state);

            let collateralType = { ...requestSettings.collateralType };
            if (empty(collateralType.type)) collateralType.type = null;
            if (
                empty(collateralType.ccy) ||
                collateralType.type === COLLAT_TYPES.NC
            )
                delete collateralType.cash;
            if (empty(collateralType.nonCash.schedule))
                delete collateralType.nonCash;

            if (enableBatching) {
                const rowCount = TxSelectors.TxInitRowData(state).length;
                const apiUrl = `${AuthSelectors.BuildTxBorrowStartBatchUrl(
                    getState()
                )}/${rowCount}`;
                // Execute API calls for each lender concurrently
                Promise.all(
                    selectedCptys.map((lender) =>
                        makeStartBatchAPICall(dispatch, lender, apiUrl)
                    )
                )
                    .then(async (responses) => {
                        // Process each response individually
                        responses.forEach(async (response, index) => {
                            const lender = [selectedCptys[index]];
                            const transactionIds = response;
                            createRowData = prepareRowData(
                                TxSelectors.TxInitRowData(state),
                                collateralType,
                                requestSettings,
                                state,
                                transactionIds,
                                enablePurpose
                            );
                            const { request, apiUrl } =
                                await prepareSendInitiateRequest(
                                    state,
                                    createRowData,
                                    lender, // Pass only one lender here
                                    requestSettings
                                );

                            // Dispatch sendInitiate action for each lender
                            dispatch(
                                WebWorkerActions.sendInitiate({
                                    cmd: "start",
                                    requestBody: request,
                                    apiUrl: apiUrl,
                                    enableBatching: enableBatching,
                                })
                            );
                        });
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            } else {
                createRowData = prepareRowData(
                    TxSelectors.TxInitRowData(state),
                    collateralType,
                    requestSettings,
                    state,
                    undefined,
                    enablePurpose
                );

                const { request, apiUrl } =
                    await prepareSendInitiateRequest(
                        state,
                        createRowData,
                        selectedCptys,
                        requestSettings
                    );

                return dispatch(
                    WebWorkerActions.sendInitiate({
                        cmd: "start",
                        requestBody: request,
                        apiUrl: apiUrl,
                    })
                );
            }
        };
    },

    SendOffer: () => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch(SpinnerActions.Open());
            dispatch({ type: Tx.SEND_INIT });

            if (!TxSelectors.TxInitIsReady(state)) {
                dispatch({ type: Tx.CANCEL_SEND });
                dispatch(ModalActions.Close());
                dispatch(ModalActions.CloseAll());
                dispatch(SpinnerActions.Close());
                return Promise.resolve();
            }

            // /ui/tx/lender/offer/create deserialises `borrower` into a
            // Counterparty struct. Match the old-arch contract — ship
            // the whole resolved cpty (id + name + partner + …) so the
            // server side is byte-for-byte compatible with what truweb-ui
            // was sending before. State stores ids only (post the
            // SelectOneAndOnlyCpty normalization), so use the
            // id→obj-resolving selector here.
            const selectedCpty = TxSelectors.TxInitSelectedCptys(state)[0];
            if (empty(selectedCpty)) {
                dispatch({ type: Tx.SET_CPTY_ERROR, value: true });
                dispatch({ type: Tx.CANCEL_SEND });
                dispatch(SpinnerActions.Close());
                return Promise.resolve();
            } else {
                dispatch({ type: Tx.SET_CPTY_ERROR, value: false });
            }

            // `borrower: selectedCpty` below ships the whole cpty as-is.
            const offerSettings = TxSelectors.TxInitOfferSettings(state);
            const offerComment = TxSelectors.TxInitOfferComment(state);

            const createRowData = TxSelectors.TxInitRowData(state).map(
                (row) => {
                    let rowOutput = {
                        quantity: row.Quantity,
                        security: row.marketData.data,
                        listName: row.ListName,
                        fee: { rateBp: Math.round(row.CalculatedRate * 100) }, // Rebate option is not offered
                        valueDate: setDateFormat(row.ValueDate, "-"),
                        txType: row.TxType || TX_TYPE_VALUES.ANY,
                        intermediary:
                            row.Intermediary || INTERMEDIARY_SELECT_TYPES.ANY,
                        maturityDate:
                            setDateFormat(row.Maturity, "-") || "9999-12-31",
                        callable: row.Callable,
                        dividend: row.Dividend,
                        collateralType: {
                            type:
                                COLLAT_TYPES[row.CollateralType] ??
                                COLLAT_TYPES.NC,
                            ccy: row.CollateralCCY ? [row.CollateralCCY] : [],
                        },
                        // common to all the rows
                        type: offerSettings.type,
                        ...(offerSettings.objective && {
                            objective: offerSettings.objective,
                        }),
                        lastUser: AuthSelectors.User(state).attributes.email,
                    };
                    return rowOutput;
                }
            );

            const url = AuthSelectors.BuildTxLenderOfferUrl(state);

            const request = {
                user: {
                    email: AuthSelectors.User(state).attributes.email,
                },
                borrower: selectedCpty,
                txSet: createRowData,
                comment: offerComment,
            };
            return dispatch(
                WebWorkerActions.sendOffer({
                    cmd: "start",
                    requestBody: request,
                    apiUrl: url,
                })
            );
        };
    },

    ApplyBenchmarkFee: (multiplier) => ({
        type: Tx.APPLY_REF_RATE,
        multiplier,
    }),

    ApplyMaturity: (maturity, enable90DayMaxMaturity) => ({
        type: Tx.APPLY_MATURITY,
        maturity,
        enable90DayMaxMaturity,
    }),

    ApplyTxType: (txType) => ({
        type: Tx.APPLY_TX_TYPE,
        txType,
    }),

    ApplyIntermediary: (intermediary) => ({
        type: Tx.APPLY_INTERMEDIARY,
        intermediary,
    }),

    BuildOfferDraft: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            return dispatch(
                fetchWrapper(AuthSelectors.BuildOfferDraftUrl(getState()), {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            )
                .then((result) => {
                    const currEntries = TxSelectors.TxRawEntries(getState());
                    if (empty(result)) {
                        dispatch(SpinnerActions.Close());
                        return dispatch(
                            ConfirmActions.Open({
                                message:
                                    "There is no inventory available that matches the open Needs Adverts.",
                                backdrop: true,
                                title: "Info",
                                testId: "nothing-to-offer",
                            })
                        );
                    }
                    const newEntries = result.flatMap((draftsPerBorrower) =>
                        draftsPerBorrower.txSet.map((tx) =>
                            buildDraftOffer(
                                getState(),
                                draftsPerBorrower.borrower,
                                tx
                            )
                        )
                    );
                    dispatch(
                        TxActions.UpdateLendListUiEdit(
                            currEntries.concat(newEntries.filter((e) => e))
                        )
                    );
                    dispatch(SpinnerActions.Close());
                })
                .catch((error) => {
                    dispatch(SpinnerActions.Close());
                    dispatch({ type: Tx.INIT_ERROR, error });
                });
        };
    },

    UpdateBorrowListUiEdit: (entries) => {
        return (dispatch) => {
            return dispatch({
                type: Tx.UPDATE_BORROW_LIST_UI_EDIT,
                entries,
            });
        };
    },
    UpdateLendListUiEdit: (entries) => {
        return (dispatch) => {
            return dispatch({
                type: Tx.UPDATE_LEND_LIST_UI_EDIT,
                entries,
            });
        };
    },

    UpsertBorrowList: (action) => {
        return (dispatch, getState) => {
            let txBorrowEntries = TxSelectors.TxBorrowEntries(getState());
            // console.log("previous txBorrowEntries", txBorrowEntries);
            const txIds = txBorrowEntries.map((t) => t.id);
            for (let i = 0; i < action.txEntries.length; i++) {
                if (txIds.includes(action.txEntries[i].id)) {
                    txBorrowEntries = updateBorrowerEntries(txBorrowEntries, {
                        tx: action.txEntries[i],
                        domainId: action.txEntries[i].id,
                        state: action.state,
                    });
                } else {
                    txBorrowEntries = [
                        ...txBorrowEntries,
                        mapBorrowEntry(action.txEntries[i]),
                    ];
                }
            }
            return dispatch({
                type: Tx.UPDATE_TX_BORROW_ENTRIES,
                txBorrowEntries,
            });
        };
    },

    UpdateUnsolicitedOffers: (action) => {
        return (dispatch, getState) => {
            const needIds = action.txEntries.map((t) => t.needId);
            let txBorrowEntries = TxSelectors.TxBorrowEntries(
                getState()
            ).filter((n) => {
                if (n.state !== TX_STATES.PRE_LOAN_REQUESTED) return true;
                const idx = needIds.indexOf(n.needId);
                if (idx < 0) return true;
                return n.lender?.id !== action.txEntries[idx].lender?.id;
            });
            const txIds = txBorrowEntries.map((t) => t.id);
            for (let i = 0; i < action.txEntries.length; i++) {
                if (txIds.includes(action.txEntries[i].id)) {
                    txBorrowEntries = updateBorrowerEntries(txBorrowEntries, {
                        tx: action.txEntries[i],
                        domainId: action.txEntries[i].id,
                        state: action.state,
                    });
                } else {
                    txBorrowEntries = [
                        ...txBorrowEntries,
                        mapBorrowEntry(action.txEntries[i]),
                    ];
                }
            }
            // console.log("JM: Got the unsolicited offers: ", txBorrowEntries);
            return dispatch({
                type: Tx.UPDATE_TX_BORROW_ENTRIES,
                txBorrowEntries,
            });
        };
    },

    UpdateLendOffers: (action) => {
        return (dispatch, getState) => {
            let txLendEntries = TxSelectors.TxLendEntries(getState()).filter(
                (n) =>
                    !(
                        n.state === TX_STATES.SENDING &&
                        n.correlationId === action.correlationId
                    )
            );
            const ids = txLendEntries.map((t) => t.id);
            for (let i = 0; i < action.txEntries.length; i++) {
                if (ids.includes(action.txEntries[i].id)) {
                    txLendEntries = updateLendEntries(txLendEntries, {
                        tx: action.txEntries[i],
                        domainId: action.txEntries[i].id,
                        state: action.state,
                    });
                } else {
                    txLendEntries = [
                        ...txLendEntries,
                        mapLendEntry(action.txEntries[i]),
                    ];
                }
            }

            return dispatch({
                type: Tx.UPDATE_TX_LEND_ENTRIES,
                txLendEntries,
            });
        };
    },

    UpsertLendTxList: (action) => {
        return (dispatch, getState) => {
            let txLendEntries = TxSelectors.TxLendEntries(getState());
            const ids = txLendEntries.map((t) => t.id);
            for (let i = 0; i < action.txEntries.length; i++) {
                if (ids.includes(action.txEntries[i].id)) {
                    txLendEntries = updateLendEntries(txLendEntries, {
                        tx: action.txEntries[i],
                        domainId: action.txEntries[i].id,
                        state: action.state,
                    });
                } else {
                    txLendEntries = [
                        ...txLendEntries,
                        mapLendEntry(action.txEntries[i]),
                    ];
                }
            }

            return dispatch({
                type: Tx.UPDATE_TX_LEND_ENTRIES,
                txLendEntries,
            });
        };
    },

    /* table */
    SetSelectedSecurity: (txId) => {
        return (dispatch, getState) => {
            let txEntries = TxSelectors.TxRawEntries(getState()).find(
                (e) => e.id === txId
            );
            if (txEntries) {
                const selectedSecurity = TxSelectors.SelectedSecurity(
                    getState()
                );
                const { security } = txEntries;
                if (!security) return;
                if (
                    !selectedSecurity ||
                    selectedSecurity.ticker !== security.ticker
                ) {
                    return dispatch({
                        type: Tx.SET_SELECTED_SECURITY,
                        security,
                    });
                }
            }
        };
    },
    SetSelectedRowIds: (rows, updateOfferedEconomics) => {
        return (dispatch, getState) => {
            if (!Array.isArray(rows)) {
                console.error("Invalid or empty rows array.", rows);
                return;
            }
            const selectedRowIds = rows.reduce(
                (selectedRowIds, r) =>
                    selectedRowIds.concat(
                        r.data
                            ? [r.data.id]
                            : r.childrenAfterFilter.map((c) => c.data.id)
                    ),
                []
            );

            // filter out prev selected rows and child rows
            const prevSelectedRowIds = TxSelectors.SelectedRowIds(getState());
            const newSelectedRowIds = selectedRowIds.filter(
                (id) =>
                    !prevSelectedRowIds.includes(id) &&
                    (typeof id !== "string" || !id.includes("#"))
            );

            dispatch(TxActions.PopulateDefaults(newSelectedRowIds));

            /**
             * This one will trigger every selection of a row and create a narrowing of isRowSelectionDisabled
             * */
            dispatch({
                type: Tx.SET_SELECTED_ROW_INFO,
                value:
                    rows.length > 0
                        ? rows[0].data
                            ? rows[0].data.isEAActionable
                            : rows[0].childrenAfterFilter[0].data.isEAActionable
                        : null,
                status: rows[0]?.data?.Status || null,
            });
            return dispatch({
                type: Tx.SET_SELECTED_ROWS,
                role: TopBarSelectors.Role(getState()),
                rowIds: [...new Set(selectedRowIds)],
                updateOfferedEconomics,
            });
        };
    },

    UpdateSelectedRowIds: (rowIds) => {
        return (dispatch, getState) => {
            return dispatch({
                type: Tx.SET_SELECTED_ROWS,
                role: TopBarSelectors.Role(getState()),
                rowIds: [...new Set(rowIds)],
            });
        };
    },

    PopulateDefaults: (txIds) => (dispatch, getState) => {
        const role = TopBarSelectors.Role(getState());
        if (role === ROLE.LENDER) {
            const txLendEntries = TxSelectors.TxLendEntries(getState());
            // set default objectives
            const peerCptySettings = SettingsSelectors.PeerCptySettings(
                getState()
            ).reduce((acc, cpty) => ({ ...acc, [cpty.cptyCode]: cpty }), {});

            const newEntries = txLendEntries
                .filter((e) => txIds.includes(e.id))
                .map((e) => ({
                    ...e,
                    objective:
                        peerCptySettings[e.borrower.id]?.defaultObjective,
                }));
            dispatch({
                type: Tx.UPDATE_LEND_LIST_UI_EDIT,
                entries: newEntries,
            });
        } else if (role === ROLE.BORROWER) {
            const {
                "enable-90-day-max-maturity-date": enable90DayMaxMaturity,
            } = SettingsSelectors.FeatureFlags(getState());

            // set default purpose & maturity based on purpose
            const txBorrowerEntries = TxSelectors.TxBorrowEntries(getState());

            const newEntries = txBorrowerEntries
                .filter((e) => txIds.includes(e.id))
                .map((e) => {
                    const defaultPurpose = getDefaultTxPurposeForCountryCode(
                        e.security.country
                    );
                    const maxMaturity = getClampedMaturityDate(
                        e.security.country,
                        defaultPurpose,
                        e.offeredEconomics.maturityDate,
                        e.offeredEconomics.valueDate,
                        e.security.tradableDates,
                        e.security.maxMaturityDates,
                        enable90DayMaxMaturity
                    );
                    const newEntry = {
                        ...e,
                        purpose: defaultPurpose,
                        offeredEconomics: {
                            ...e.offeredEconomics,
                            maturityDate: maxMaturity,
                        },
                    };

                    return newEntry;
                });
            dispatch({
                type: Tx.UPDATE_BORROW_LIST_UI_EDIT,
                entries: newEntries,
            });
        }
    },

    AddOffer: (rowId) => {
        return (dispatch, getState) => {
            let txLendEntries = TxSelectors.TxLendEntries(getState());

            const { parentEntry, childEntries, index } =
                TxSelectors.GetChildEntries(getState(), rowId);
            const newOffers = buildNewOffers(
                rowId,
                parentEntry,
                childEntries,
                index
            );
            dispatch({
                type: Tx.UPDATE_TX_LEND_ENTRIES,
                txLendEntries: txLendEntries.concat(newOffers),
            });
        };
    },

    ResetOfferingTxs: () => {
        return (dispatch, getState) => {
            const role = TopBarSelectors.Role(getState());
            if (role !== ROLE.LENDER) return;
            dispatch({
                type: Tx.UPDATE_TX_LEND_ENTRIES,
                txLendEntries: [],
            });
            dispatch(TxActions.SetSelectedRowIds([], false));
        };
    },

    DeleteOffer: (id, preloanId) => {
        return (dispatch, getState) => {
            let txLendEntries = TxSelectors.TxLendEntries(getState());
            let selectedRowIds = TxSelectors.SelectedRowIds(getState());
            if (preloanId) {
                const { parentEntry, childEntries } =
                    TxSelectors.GetChildEntries(getState(), preloanId);
                if (childEntries?.length === 2) {
                    const unselected = childEntries.find((c) => c.id !== id);
                    txLendEntries = txLendEntries.filter(
                        (e) =>
                            e.id !== id &&
                            e.id !== unselected.id &&
                            e.id !== preloanId
                    );
                    txLendEntries = [
                        ...txLendEntries,
                        {
                            ...parentEntry,
                            listName: unselected.listName,
                            offeredEconomics: {
                                ...unselected.offeredEconomics,
                            },
                            keepOffer: true,
                        },
                    ];
                    return dispatch({
                        type: Tx.UPDATE_TX_LEND_ENTRIES,
                        txLendEntries,
                    });
                }
            }
            dispatch(
                TxActions.UpdateSelectedRowIds(
                    selectedRowIds.filter((r) => r !== id)
                )
            );
            dispatch({
                type: Tx.UPDATE_TX_LEND_ENTRIES,
                txLendEntries: txLendEntries.filter((e) => e.id !== id),
            });
        };
    },
};
